import { useState } from 'react';
import { Wordmark } from '../brand/Brand';
import type { User } from '../api';
import * as api from '../api';

export function UpgradePage({ user }: { user: User }) {
  const [interval, setInterval] = useState<'yearly' | 'monthly'>('yearly');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const { url } = await api.checkout(interval);
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="upgrade-page">
      <div className="upgrade-container">
        <a href="/app" className="upgrade-back">&larr; back to app</a>

        <div className="upgrade-header">
          <Wordmark size={22} />
          <h1 className="upgrade-title">Upgrade to Pro</h1>
          <p className="upgrade-subtitle">Unlimited ideas, and the whole trail from idea to shipped.</p>
        </div>

        {/* Billing toggle */}
        <div className="upgrade-toggle">
          <button
            className={`plan-interval-btn${interval === 'yearly' ? ' plan-interval-active' : ''}`}
            onClick={() => setInterval('yearly')}
          >
            yearly
          </button>
          <button
            className={`plan-interval-btn${interval === 'monthly' ? ' plan-interval-active' : ''}`}
            onClick={() => setInterval('monthly')}
          >
            monthly
          </button>
        </div>

        {/* Cards */}
        <div className="upgrade-cards">
          {/* Free card */}
          <div className="plan-card plan-card-free upgrade-card">
            <span className="plan-badge plan-badge-free">Free</span>
            <div className="plan-price">
              <span className="plan-price-amount plan-price-free">$0</span>
              <span className="plan-price-period"> forever</span>
            </div>
            <ul className="plan-features plan-features-free">
              <li><span className="plan-check plan-check-muted">&#10003;</span> 50 ideas</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Web app</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Claude MCP connector</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Claude Code skill</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> nrepo CLI</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Keyword search</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Semantic search (10/mo)</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Basic auto-tagging</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Kanban board</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Timeline + session records</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Idea relations (view)</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> Mind map (view)</li>
              <li><span className="plan-check plan-check-muted">&#10003;</span> JSON / CSV export</li>
            </ul>
            <ul className="plan-anti-features">
              <li>&mdash; No duplicate detection</li>
              <li>&mdash; No GitHub connection</li>
            </ul>
            <div className="upgrade-current-plan">
              {user.plan === 'free' && !user.is_trialing ? 'Current plan' : ''}
            </div>
          </div>

          {/* Pro card */}
          <div className="plan-card plan-card-pro upgrade-card">
            <span className="plan-badge plan-badge-pro">Pro</span>
            <div className="plan-price">
              {interval === 'yearly' ? (
                <>
                  <span className="plan-price-amount">$70</span>
                  <span className="plan-price-period">/year</span>
                  <span className="plan-price-sub">$5.83/mo</span>
                </>
              ) : (
                <>
                  <span className="plan-price-amount">$7</span>
                  <span className="plan-price-period">/month</span>
                </>
              )}
            </div>
            <ul className="plan-features">
              <li><span className="plan-check">&#10003;</span> Unlimited ideas</li>
              <li><span className="plan-check">&#10003;</span> All capture methods</li>
              <li><span className="plan-check">&#10003;</span> Unlimited semantic search</li>
              <li><span className="plan-check">&#10003;</span> Duplicate detection</li>
              <li><span className="plan-check">&#10003;</span> Auto-tagging</li>
              <li><span className="plan-check">&#10003;</span> Full mind map + relations</li>
              <li><span className="plan-check">&#10003;</span> GitHub: commits + PRs on your ideas</li>
              <li><span className="plan-check">&#10003;</span> GitHub issues + Claude builds</li>
              <li><span className="plan-check">&#10003;</span> Timeline with code activity</li>
              <li><span className="plan-check">&#10003;</span> Siri Shortcuts</li>
              <li><span className="plan-check">&#10003;</span> Email capture</li>
              <li><span className="plan-check">&#10003;</span> BYOK support</li>
              <li><span className="plan-check">&#10003;</span> API access</li>
            </ul>
            {user.plan === 'pro' && !user.is_trialing ? (
              <div className="upgrade-current-plan upgrade-current-pro">Current plan</div>
            ) : (
              <button
                className="btn btn-primary plan-cta"
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? 'Redirecting...' : user.is_trialing ? 'Keep Pro — Subscribe' : 'Subscribe to Pro'}
              </button>
            )}
          </div>
        </div>

        {/* Feature comparison table */}
        <div className="upgrade-comparison">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Pro ($7/mo)</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Ideas</td><td>50</td><td>Unlimited</td></tr>
              <tr><td>Web app</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>Claude MCP connector</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>Claude Code skill</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>nrepo CLI</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>Siri Shortcuts</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>Email capture</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>Keyword search</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>Semantic search</td><td>10/month</td><td>Unlimited</td></tr>
              <tr><td>Duplicate detection</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>Auto-tagging</td><td>Basic</td><td>Full</td></tr>
              <tr><td>Kanban board</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>Idea relations</td><td>View</td><td>Full</td></tr>
              <tr><td>Mind map</td><td>View</td><td>Full</td></tr>
              <tr><td>Timeline</td><td>Your ideas</td><td>+ commits &amp; PRs</td></tr>
              <tr><td>Session records</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>GitHub: commits &amp; PRs on ideas</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>GitHub issues &amp; Claude builds</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>JSON/CSV export</td><td>&#10003;</td><td>&#10003;</td></tr>
              <tr><td>BYOK (own API keys)</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>API access</td><td>&mdash;</td><td>&#10003;</td></tr>
              <tr><td>Feature request voting</td><td>&#10003;</td><td>&#10003;</td></tr>
            </tbody>
          </table>
        </div>

        <p className="upgrade-trial-note">
          All new accounts include a 30-day Pro trial &mdash; experience every feature before deciding.
        </p>

        <p className="upgrade-footer">
          Payments processed by Stripe. Cancel anytime.
          {' '}<a href="mailto:support@neuralrepo.com">support@neuralrepo.com</a>
        </p>
      </div>
    </div>
  );
}
